import React, { useState } from 'react';
import { Upload, Info } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { MondrianPanel } from './ui/MondrianPanel';
import { MondrianGrid } from './MondrianGrid';
export function EvolutionSlider() {
  const [image, setImage] = useState<string | null>(null);
  const [level, setLevel] = useState(0);
  const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = e => {
        setImage(e.target?.result as string);
        setLevel(0);
      };
      reader.readAsDataURL(file);
    }
  };
  const levels = [{
    id: 0,
    title: '写实 (Realism)',
    desc: '具象的自然形态，充满细节与偶然性。',
    color: 'bg-mondrian-black'
  }, {
    id: 1,
    title: '表现 (Expression)',
    desc: '色彩增强，笔触狂野，开始脱离客观再现。',
    color: 'bg-mondrian-yellow'
  }, {
    id: 2,
    title: '立体 (Cubism)',
    desc: '形体破碎，只保留线条与块面，色彩开始简化。',
    color: 'bg-mondrian-blue'
  }, {
    id: 3,
    title: '纯粹 (Pure)',
    desc: '最终的本质。只有水平与垂直，三原色与非色。',
    color: 'bg-mondrian-red'
  }];
  return <div className="space-y-8" data-id="element-134">
      <div className="text-center space-y-2" data-id="element-135">
        <h2 className="text-2xl font-bold uppercase" data-id="element-136">工具四：抽象时光机</h2>
        <p className="text-gray-600" data-id="element-137">拖动滑块，见证从具象到抽象的演变</p>
      </div>

      <div className="bg-blue-50 border-l-4 border-mondrian-blue p-4 text-sm text-gray-700 italic" data-id="element-138">
        "蒙德里安不是生来就画格子的。就像一棵树在寒冬脱去叶子，他的艺术也经历了从具象到抽象的蜕变。拖动滑块，亲眼见证这场'视觉的脱衣舞'。"
      </div>

      {/* Main Display Area */}
      <div className="grid md:grid-cols-3 gap-8" data-id="element-139">
        <div className="md:col-span-2 aspect-square relative border-8 border-mondrian-black bg-gray-100 overflow-hidden" data-id="element-140">
          {!image ? <div className="absolute inset-0 flex flex-col items-center justify-center border-dashed border-4 border-gray-300 m-4" data-id="element-141">
              <input type="file" accept="image/*" onChange={handleUpload} className="absolute inset-0 opacity-0 cursor-pointer z-10" data-id="element-142" />
              <div className="bg-mondrian-black p-4 mb-4" data-id="element-143">
                <Upload className="w-8 h-8 text-white" data-id="element-144" />
              </div>
              <p className="font-bold text-lg" data-id="element-145">上传一张照片开始</p>
              <p className="text-sm text-gray-500" data-id="element-146">建议使用树木或风景照</p>
            </div> : <AnimatePresence mode="wait" data-id="element-147">
              <motion.div key={level} initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.5
          }} className="w-full h-full relative" data-id="element-148">
                {level === 0 && <img src={image} alt="Realism" className="w-full h-full object-cover" data-id="element-149" />}

                {level === 1 && <div className="w-full h-full relative overflow-hidden" data-id="element-150">
                    <img src={image} alt="Expressionism" className="w-full h-full object-cover filter contrast-125 saturate-200 hue-rotate-15" data-id="element-151" />
                    <div className="absolute inset-0 bg-orange-500/20 mix-blend-overlay" data-id="element-152" />
                  </div>}

                {level === 2 && <div className="w-full h-full relative overflow-hidden bg-gray-200" data-id="element-153">
                    <img src={image} alt="Cubism" className="w-full h-full object-cover filter grayscale contrast-150 opacity-50" data-id="element-154" />
                    {/* Cubist Lines Overlay */}
                    <div className="absolute inset-0 opacity-60" data-id="element-155">
                      {[...Array(10)].map((_, i) => <div key={i} className="absolute border-black border-2" style={{
                  left: `${Math.random() * 80}%`,
                  top: `${Math.random() * 80}%`,
                  width: `${20 + Math.random() * 40}%`,
                  height: `${20 + Math.random() * 40}%`,
                  borderRadius: '50%',
                  borderColor: i % 2 === 0 ? 'black' : 'gray'
                }} data-id="element-156" />)}
                    </div>
                  </div>}

                {level === 3 && <MondrianGrid density="high" animated seed={image.length} data-id="element-157" />}
              </motion.div>
            </AnimatePresence>}

          {/* Level Label Overlay */}
          <div className="absolute top-0 left-0 bg-mondrian-black text-white px-4 py-2 font-bold text-lg border-b-4 border-r-4 border-white" data-id="element-158">
            LEVEL {level}: {levels[level].title.split(' ')[0]}
          </div>
        </div>

        {/* Controls Side */}
        <div className="flex flex-col justify-center space-y-8" data-id="element-159">
          <MondrianPanel className="space-y-6" data-id="element-160">
            <div className="space-y-2" data-id="element-161">
              <h3 className="font-bold text-xl border-b-4 border-mondrian-black inline-block mb-2" data-id="element-162">
                抽象程度 (Abstraction)
              </h3>

              {/* Custom Slider */}
              <div className="relative h-12 w-full bg-gray-200 border-4 border-mondrian-black flex" data-id="element-163">
                {levels.map(l => <div key={l.id} onClick={() => image && setLevel(l.id)} className={`flex-1 cursor-pointer transition-colors ${level >= l.id ? l.color : 'bg-transparent'} border-r-2 border-black last:border-r-0 relative group`} data-id="element-164">
                    {/* Hover tooltip */}
                    <div className="absolute -top-10 left-1/2 -translate-x-1/2 bg-black text-white text-xs px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none" data-id="element-165">
                      {l.title}
                    </div>
                  </div>)}

                {/* Thumb */}
                <motion.div className="absolute top-0 bottom-0 w-1/4 border-4 border-white shadow-[0_0_0_4px_black] pointer-events-none" animate={{
                left: `${level * 25}%`
              }} transition={{
                type: 'spring',
                stiffness: 300,
                damping: 30
              }} data-id="element-166" />
              </div>

              <div className="flex justify-between text-xs font-bold mt-1" data-id="element-167">
                <span data-id="element-168">写实</span>
                <span data-id="element-169">纯粹</span>
              </div>
            </div>

            <div className="bg-gray-50 p-4 border-2 border-mondrian-black" data-id="element-170">
              <div className="flex items-start gap-3" data-id="element-171">
                <Info className="w-6 h-6 text-mondrian-blue flex-shrink-0 mt-1" data-id="element-172" />
                <div data-id="element-173">
                  <h4 className="font-bold text-mondrian-blue mb-1" data-id="element-174">
                    {levels[level].title}
                  </h4>
                  <p className="text-sm text-gray-600 leading-relaxed" data-id="element-175">
                    {levels[level].desc}
                  </p>
                </div>
              </div>
            </div>
          </MondrianPanel>
        </div>
      </div>
    </div>;
}